// src/context/WalletContext.js
// Wallet context - thin facade over wagmi for app-wide wallet state.
// Public API unchanged from the previous version, so existing components keep working.
//
// ADDED (onboarding tier 1 + 2):
//   - Silent user provisioning at connect: getOrCreateByWallet runs here,
//     the single canonical creation point, WITH the stored referral code —
//     fixing the attribution leak where ProfilePage created users codeless.
//   - Connect intent: purchase flows call setConnectIntent('purchase')
//     before connectWallet() so the profile nudge never interrupts a buy.
//   - ProfileSetupModal: shown exactly once ever (isNew === true), fully
//     skippable. ProfilePage/KOLDetailPage keep their getOrCreateByWallet
//     calls — those are now idempotent reads (isNew: false, no popup).
//     When it closes (saved or skipped) the window event lunexx:new-member
//     fires: AddFundsProvider opens the welcome offer if the account is
//     eligible (mission 008, decision 008-02 §4b). A purchase-intent connect
//     shows no nudge and fires nothing (the buy is never interrupted).
//
// EMAIL SIGN-IN (mission 007, REACT_APP_EMAIL_SIGNIN=on): walletAddress /
// isConnected reflect the ACTIVE session — for an email session the account's
// managed address and true, with canSign false (no wagmi account: nothing can
// sign or deposit). The server provisioned that account, so the silent
// provisioning below never runs for it (its row is only read). The modal
// rendered here is then SignInModal (email + wallet); a signed-in email user
// is not offered it. disconnectWallet (the navbar Log out) also ends an email
// session.
//
// SIGN-IN STEP (mission 009, Fable decision 009-01): a connect from either
// modal does not close it. While the wagmi wallet is connected and unsigned the
// modal shows "Confirm it's you" (SignInStep), whose Sign in tap requests the
// SIWE signature — a popup wallet's request must come from a tap — and the
// modal closes once the session exists. That is why WalletProvider sits INSIDE
// AuthSiweProvider (App.js): it reads useSiweAuth(). Nothing in AuthContext
// reads this context.

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ExternalLink, Wallet, Check, Sparkles } from 'lucide-react';
import {
  useAccount,
  useBalance,
  useChainId,
  useConnect,
  useDisconnect,
  useSwitchChain,
  useConfig,
} from 'wagmi';
import {
  sendTransaction,
} from 'wagmi/actions';
import { baseSepolia } from 'wagmi/chains';
import { userService } from '../services/UserService';
import ProfileSetupModal from '../components/onboarding/ProfileSetupModal';

import { parseEther } from 'viem';
import { wagmiConfig } from '../config/wagmi';
import { authService, markConnectIntent } from '../services/AuthService';
import { EMAIL_PAYMENT_COPY, emailSignInEnabled } from '../config/emailSignIn';
import { useAuthSession } from '../hooks/useAuthSession';
import { endEmailSession } from '../lib/emailSession';
import SignInModal from '../components/SignInModal';
import SignInStep, { useSignInStep } from '../components/SignInStep';
import { useSiweAuth } from './AuthContext';

const SUPPORTED_CHAIN_IDS = {
  MAINNET: '0x1',
  SEPOLIA: '0xaa36a7',
  BASE_SEPOLIA: '0x14a34',
};

const DEFAULT_CHAIN = baseSepolia;
// (MOBILE_RX removed with the dead MetaMask deep-link branch — mobile
// users get the modal, where the passkey Smart Wallet works in-browser.)

// ----------------------------------------------------------------
// Connect intent — purchase-initiated connects suppress the profile
// nudge. Module scope so non-context callers (overlays, RoomPage) can
// flip it without a hook.
// ----------------------------------------------------------------
let connectIntent = 'default'; // 'default' | 'purchase'
export const setConnectIntent = (intent) => {
  connectIntent = intent === 'purchase' ? 'purchase' : 'default';
};

const WalletContext = createContext();

export const useWallet = () => {
  const ctx = useContext(WalletContext);
  if (!ctx) throw new Error('useWallet must be used within a WalletProvider');
  return ctx;
};

/** The ETH balance, fetched only while a component using this is mounted. */
export const useWalletBalance = () => {
  const { ethBalance, watchBalance } = useWallet();
  useEffect(() => watchBalance(), [watchBalance]);
  return ethBalance;
};

export const WalletProvider = ({ children }) => {
  const wagmiCfg = useConfig();
  const { address, isConnected, connector: activeConnector } = useAccount();
  const flagged = emailSignInEnabled();
  const authSession = useAuthSession();
  const emailSession = flagged && authSession?.method === 'email' ? authSession : null;
  const emailWallet = emailSession?.walletAddress ?? null;
  const chainId = useChainId();
  const { connectors, connectAsync, isPending: isConnectPending, error: connectError } = useConnect();
  const { disconnectAsync } = useDisconnect();
  const { switchChainAsync } = useSwitchChain();
  // The balance RPC runs only while a surface that shows it is mounted
  // (useWalletBalance registers itself); otherwise every page polled it.
  const [balanceViewers, setBalanceViewers] = useState(0);
  const { data: balance } = useBalance({ address, query: { enabled: !!address && balanceViewers > 0 } });
  const watchBalance = useCallback(() => {
    setBalanceViewers((n) => n + 1);
    return () => setBalanceViewers((n) => Math.max(0, n - 1));
  }, []);

  const [showWalletModal, setShowWalletModal] = useState(false);
  // a connected, unsigned wagmi wallet: the modal shows "Confirm it's you"
  const { isAuthenticated } = useSiweAuth();
  const signIn = useMemo(() => ({ needed: !!isConnected && !!address && !isAuthenticated, address }), [isConnected, address, isAuthenticated]);
  // a user-started connect from the modal: mark the intent (AuthContext asks an
  // extension wallet at once), keep the modal open on the step unless this
  // account already holds a session
  const connectFromModal = useCallback(async (connector) => {
    try {
      markConnectIntent();
      const result = await connectAsync({ connector });
      const account = result?.accounts?.[0];
      if (account && authService.getToken(account)) setShowWalletModal(false);
    } catch (err) {
      console.error('Failed to connect:', err);
    }
  }, [connectAsync]);
  const switchWallet = useCallback(() => disconnectAsync().catch(() => {}), [disconnectAsync]);
  const [error, setError] = useState(null);

  // Onboarding state
  const [showProfileSetup, setShowProfileSetup] = useState(false);
  const [provisionedUser, setProvisionedUser] = useState(null);
  const provisionedRef = useRef(null); // last wallet provisioned this session

  // The connected wallet's `users` row. Provisioning already fetched this
  // but used to throw it away for returning users — now exposed so the
  // sidebar (VerticalNavbar) and others can show avatar/username (P1.3).
  const [currentUser, setCurrentUser] = useState(null);

  // ----------------------------------------------------------------
  // SILENT PROVISIONING — runs once per wallet per session, including
  // wagmi's auto-reconnect on page load (returning users hit the
  // `existing` branch: isNew false, no popup, fully idempotent).
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!isConnected || !address) return;
    const wallet = address.toLowerCase();
    if (provisionedRef.current === wallet) return;
    provisionedRef.current = wallet;

    let cancelled = false;
    (async () => {
      try {
        // Referral code stashed by the access-gate / landing flow.
        // Canonical key: 'lunexx_referral_code' (written by UserService's
        // ?ref= capture, AccessGatePage, LandingPage, AccessCodeGate).
        // 'lunexx_ref' kept as a legacy fallback.
        const refCode =
          localStorage.getItem('lunexx_referral_code') ||
          sessionStorage.getItem('lunexx_referral_code') ||
          localStorage.getItem('lunexx_ref') ||
          null;

        const { data: user, isNew, needsSignIn } = await userService.getOrCreateByWallet(address, refCode);
        // Not signed in yet: the row is created at sign-in (server-side).
        // Let the sign-in event below finish provisioning.
        if (needsSignIn) provisionedRef.current = null;
        if (cancelled || !user) return;

        setCurrentUser(user);

        // Nudge exactly once ever, never mid-purchase
        if (isNew && connectIntent !== 'purchase') {
          setProvisionedUser(user);
          setShowProfileSetup(true);
        }
      } catch (err) {
        console.error('[WalletContext] user provisioning failed:', err);
      } finally {
        connectIntent = 'default'; // reset after each connect resolves
      }
    })();

    return () => { cancelled = true; };
  }, [isConnected, address]);

  // SIGN-IN PROVISIONS THE USER (Packet 04): siwe-verify returns the
  // users row it created or found; adopt it here, and show the one-time
  // profile nudge when it is brand new.
  useEffect(() => {
    const onSignedIn = (e) => {
      const user = e?.detail?.user;
      const wallet = e?.detail?.walletAddress;
      const emailSignIn = flagged && e?.detail?.method === 'email';
      if (!user || !wallet || (!emailSignIn && (!address || wallet !== address.toLowerCase()))) return;
      provisionedRef.current = wallet;
      setCurrentUser(user);
      const fresh = user.created_at && Date.now() - Date.parse(user.created_at) < 120_000;
      if (fresh && connectIntent !== 'purchase') {
        setProvisionedUser(user);
        setShowProfileSetup(true);
      }
    };
    window.addEventListener('lunexx:signed-in', onSignedIn);
    return () => window.removeEventListener('lunexx:signed-in', onSignedIn);
  }, [address, flagged]);

  // An email session's row: the server provisioned it — only READ it here
  // (a reload has no sign-in event to carry it).
  useEffect(() => {
    if (!emailWallet || isConnected || provisionedRef.current === emailWallet) return undefined;
    provisionedRef.current = emailWallet;
    let cancelled = false;
    userService.getByWallet(emailWallet)
      .then(({ data }) => { if (!cancelled && data) setCurrentUser(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [emailWallet, isConnected]);

  // Clear the user row on disconnect / wallet switch / sign-out
  useEffect(() => {
    const active = isConnected ? address : emailWallet;
    if (!active) {
      setCurrentUser(null);
      provisionedRef.current = null;
    }
  }, [isConnected, address, emailWallet]);

  const connectedWalletType = useMemo(() => {
    if (!activeConnector) return null;
    const name = activeConnector.name?.toLowerCase() || '';
    if (name.includes('metamask')) return 'metamask';
    if (name.includes('coinbase')) return 'coinbase';
    if (name.includes('phantom')) return 'phantom';
    if (name.includes('rainbow')) return 'rainbow';
    return 'injected';
  }, [activeConnector]);

  const chainIdHex = useMemo(() => {
    return chainId ? `0x${chainId.toString(16)}` : null;
  }, [chainId]);

  const ethBalance = useMemo(() => {
    if (!balance?.value) return 0;
    return Number(balance.value) / 1e18;
  }, [balance]);

  const connectWallet = useCallback(async (preferredWalletType = null) => {
    try {
      setError(null);
      // NOTE: the old `connectors.length === 0` → MetaMask deep-link
      // branch was dead code (the Coinbase SDK connector is always
      // registered) and hard-assumed MetaMask on mobile. Removed —
      // mobile users now get the modal like everyone else, where the
      // passkey Smart Wallet path works in any mobile browser.

      let target = null;
      if (preferredWalletType) {
        target = connectors.find(c => {
          const n = c.name?.toLowerCase() || '';
          if (preferredWalletType === 'metamask' && n.includes('metamask')) return true;
          // 'coinbase' AND the newcomer 'create' path both resolve to the
          // Coinbase SDK connector — its own UI offers "Create" (passkey
          // Smart Wallet) alongside app/extension sign-in.
          if ((preferredWalletType === 'coinbase' || preferredWalletType === 'create') && n.includes('coinbase')) return true;
          if (preferredWalletType === 'phantom' && n.includes('phantom')) return true;
          if (preferredWalletType === 'injected' && (c.id === 'injected' || n.includes('injected') || n.includes('browser'))) return true;
          return false;
        });
      }

      if (!target && connectors.length === 1) {
        target = connectors[0];
      }

      if (!target) {
        setShowWalletModal(true);
        return null;
      }

      markConnectIntent();
      const result = await connectAsync({ connector: target });
      return result.accounts?.[0] || null;
    } catch (err) {
      console.error('connectWallet error:', err);
      setError(err.message);
      return null;
    }
  }, [connectors, connectAsync]);

  const disconnectWallet = useCallback(async () => {
    try {
      if (flagged && authService.getSession()?.method === 'email') await endEmailSession();
      await disconnectAsync();
      setError(null);
      window.dispatchEvent(new CustomEvent('walletDisconnected'));
    } catch (err) {
      console.error('disconnectWallet error:', err);
    }
  }, [disconnectAsync, flagged]);

  const switchNetwork = useCallback(async (targetChainIdHex = SUPPORTED_CHAIN_IDS.BASE_SEPOLIA) => {
    try {
      const targetChainId = parseInt(targetChainIdHex, 16);
      if (chainId === targetChainId) return;
      await switchChainAsync({ chainId: targetChainId });
    } catch (err) {
      console.error('Failed to switch network:', err);
      throw err;
    }
  }, [chainId, switchChainAsync]);



  const sendPayment = useCallback(async ({ to, amount, onSuccess, onError }) => {
    try {
      if (!to) throw new Error('Missing recipient address');
      if (amount === undefined || amount === null || isNaN(amount)) {
        throw new Error('Missing or invalid amount');
      }
      if (!isConnected) throw new Error('Wallet not connected');

      // Switch via the ACTIVE connector — not window.ethereum
      if (chainId !== DEFAULT_CHAIN.id) {
        await switchChainAsync({ chainId: DEFAULT_CHAIN.id });
      }

      // Float-safe: tiny amounts stringify as "5.8e-7", which parseEther rejects
      const amountStr = Number(amount).toFixed(18).replace(/0+$/, '').replace(/\.$/, '');

      const txHash = await sendTransaction(wagmiConfig, {
        to,
        value: parseEther(amountStr),
        chainId: DEFAULT_CHAIN.id,
      });

      await onSuccess?.(txHash);
      return txHash;
    } catch (err) {
      console.error('Payment failed:', err);
      onError?.(err);
      throw err;
    }
  }, [isConnected, chainId, switchChainAsync]);

  const fetchEthBalance = useCallback(async () => {
    return ethBalance;
  }, [ethBalance]);

  const formatAddress = useCallback((addr) => {
    if (!addr) return '';
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  }, []);

  const getAvailableWallets = useCallback(() => {
    return connectors.map(c => ({
      name: c.name,
      type: connectedWalletType || c.id,
      provider: null,
      icon: getWalletIcon(c.name),
      installUrl: getWalletInstallUrl(c.name),
    }));
  }, [connectors, connectedWalletType]);

  const value = {
    walletAddress: address || emailWallet || null,
    currentUser,
    setCurrentUser,
    ethBalance,
    watchBalance,
    isConnected: isConnected || !!emailSession,
    // only a wagmi wallet can sign (deposits, payments); an email session cannot
    canSign: !!isConnected,
    authMethod: emailSession ? 'email' : (isConnected ? 'wallet' : null),
    email: emailSession?.email ?? null,
    isLoading: isConnectPending,
    connectedWalletType,
    chainId: chainIdHex,
    error: error || connectError?.message || null,
    connectWallet,
    disconnectWallet,
    sendPayment,
    switchNetwork,
    fetchEthBalance,
    formatAddress,
    getAvailableWallets,
    showWalletModal,
    setShowWalletModal,
    SUPPORTED_CHAIN_IDS,
    // Onboarding
    setConnectIntent,
    showProfileSetup,
    setShowProfileSetup,
    provisionedUser,
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
      {flagged ? (
        <SignInModal
          isOpen={showWalletModal && !emailSession}
          onClose={() => setShowWalletModal(false)}
          connectors={connectors}
          onConnect={connectFromModal}
          signIn={signIn}
          onUseAnother={switchWallet}
          // a connected (unsigned) wagmi wallet would end the email session at once
          beforeEmailSession={async () => { if (isConnected) await disconnectAsync().catch(() => {}); }}
          isLoading={isConnectPending}
        />
      ) : (
      <WalletModal
        isOpen={showWalletModal}
        onClose={() => setShowWalletModal(false)}
        connectors={connectors}
        onConnect={connectFromModal}
        signIn={signIn}
        onUseAnother={switchWallet}
        isLoading={isConnectPending}
      />
      )}

      {/* First-connect profile nudge — once ever, skippable */}
      <AnimatePresence>
        {showProfileSetup && provisionedUser && (
          <ProfileSetupModal
            user={provisionedUser}
            onClose={() => { setShowProfileSetup(false); announceNewMember(); }}
            onSaved={(updates) => {
              setProvisionedUser((u) => (u ? { ...u, ...updates } : u));
              setCurrentUser((u) => (u ? { ...u, ...updates } : u));
              setShowProfileSetup(false);
              announceNewMember();
            }}
          />
        )}
      </AnimatePresence>
    </WalletContext.Provider>
  );
};

/** a new member finished (or skipped) the profile nudge: the welcome offer may open */
function announceNewMember() {
  try { window.dispatchEvent(new CustomEvent('lunexx:new-member')); } catch { /* no window */ }
}

function getWalletIcon(name) {
  const n = name?.toLowerCase() || '';
  if (n.includes('metamask')) return 'https://upload.wikimedia.org/wikipedia/commons/3/36/MetaMask_Fox.svg';
  if (n.includes('coinbase')) return 'https://altcoinsbox.com/wp-content/uploads/2022/12/coinbase-logo.webp';
  if (n.includes('phantom')) return 'https://phantom.app/img/phantom-logo.svg';
  if (n.includes('rainbow')) return 'https://rainbow.me/img/rainbow-og.png';
  return 'https://img.icons8.com/color/96/ethereum.png';
}

function getWalletInstallUrl(name) {
  const n = name?.toLowerCase() || '';
  if (n.includes('metamask')) return 'https://metamask.io/download/';
  if (n.includes('coinbase')) return 'https://www.coinbase.com/wallet';
  if (n.includes('phantom')) return 'https://phantom.app/';
  if (n.includes('rainbow')) return 'https://rainbow.me/';
  return 'https://metamask.io/download/';
}

const RECOMMENDED_WALLETS = [
  { name: 'MetaMask', installUrl: 'https://metamask.io/download/', icon: 'https://upload.wikimedia.org/wikipedia/commons/3/36/MetaMask_Fox.svg', description: 'The most popular Ethereum wallet' },
  { name: 'Coinbase Wallet', installUrl: 'https://www.coinbase.com/wallet', icon: 'https://altcoinsbox.com/wp-content/uploads/2022/12/coinbase-logo.webp', description: 'Easy-to-use wallet by Coinbase' },
  { name: 'Rainbow', installUrl: 'https://rainbow.me/', icon: 'https://rainbow.me/img/rainbow-og.png', description: 'Fun and simple Ethereum wallet' },
];

const WalletModal = ({ isOpen, onClose, connectors, onConnect, signIn, onUseAnother, isLoading }) => {
  const [stepActive, endStep] = useSignInStep(!!isOpen, !!signIn?.needed);
  if (!isOpen) return null;
  const showStep = stepActive && !!signIn?.address;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
        {/* Deep blur backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/80 backdrop-blur-md"
          onClick={onClose}
        />

        {/* Modal Container */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-md overflow-hidden rounded-[34px] border border-white/[0.08] bg-gradient-to-b from-[#1E1E1E] to-[#0F0F0F] shadow-[0_32px_100px_rgba(0,0,0,0.38)]"
        >
          {/* Ambient Glows & Glare */}
          <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
          <div className="pointer-events-none absolute -right-24 -top-28 h-80 w-80 rounded-full bg-[#A3E635]/[0.035] blur-[110px]" />
          <div className="pointer-events-none absolute -bottom-32 left-1/4 h-80 w-80 rounded-full bg-fuchsia-500/[0.04] blur-[120px]" />

          {/* Header */}
          <div className="relative z-10 flex items-center justify-between px-7 py-5 border-b border-white/[0.04]">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-[20px] flex items-center justify-center shadow-inner bg-white/[0.06] border border-white/[0.12]">
                <Wallet size={20} className="text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white tracking-tight">{showStep ? 'Sign in' : 'Connect Wallet'}</h3>
                <p className="text-sm font-medium text-zinc-500">{showStep ? 'One more step' : 'Select a wallet to continue'}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="w-9 h-9 rounded-full bg-white/[0.03] border border-white/[0.06] flex items-center justify-center text-zinc-500 hover:text-white hover:bg-white/[0.08] transition-colors"
            >
              <X size={18} />
            </button>
          </div>

          {/* Body — NEWCOMER-FIRST (P-onboarding redesign):
              1. Primary: create a passkey Smart Wallet in the browser via
                 the Coinbase SDK connector — no extension, no app, no
                 seed phrase. This was always shipped but buried behind a
                 button that read like "I have the Coinbase app."
              2. Secondary: connect an existing wallet, with HONEST
                 detection (the old modal stamped "Detected" on the
                 Coinbase SDK, which is merely always-available). */}
          <div className="relative z-10 px-7 py-6 space-y-6 max-h-[60vh] overflow-y-auto custom-scrollbar">
            {showStep ? (
              <SignInStep address={signIn.address} onDone={onClose}
                onUseAnother={async () => { await onUseAnother?.(); endStep(); }} />
            ) : (() => {
              const coinbase = connectors.find((c) => (c.name || '').toLowerCase().includes('coinbase'));
              const others = connectors.filter((c) => c !== coinbase);
              const hasInjected = typeof window !== 'undefined' && !!window.ethereum;

              return (
                <>
                  {/* NEW TO CRYPTO — primary path */}
                  {coinbase && (
                    <div className="space-y-3">
                      <h4 className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider pl-1">
                        New here? No wallet needed
                      </h4>
                      <button
                        onClick={() => onConnect(coinbase)}
                        disabled={isLoading}
                        className="w-full flex items-center gap-4 p-4 rounded-[24px] bg-[#A3E635] text-black transition-all hover:bg-[#b4f146] disabled:opacity-50 shadow-[0_12px_40px_rgba(163,230,53,0.18)]"
                      >
                        <div className="grid h-10 w-10 place-items-center rounded-xl bg-black/10">
                          <Sparkles size={20} />
                        </div>
                        <div className="flex-1 text-left">
                          <span className="block text-[15px] font-bold tracking-tight">
                            Create a wallet in seconds
                          </span>
                          <span className="text-[12px] font-medium text-black/60">
                            Face ID / fingerprint passkey — no app, no seed phrase
                          </span>
                        </div>
                      </button>
                    </div>
                  )}

                  {/* EXISTING WALLETS */}
                  <div className="space-y-3">
                    <h4 className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider pl-1">
                      Already have a wallet?
                    </h4>
                    {others.map((connector) => {
                      const isInjectedConnector =
                        connector.id === 'injected' || connector.type === 'injected';
                      const detected = isInjectedConnector ? hasInjected : true;
                      return (
                        <button
                          key={connector.uid}
                          onClick={() => onConnect(connector)}
                          disabled={isLoading || (isInjectedConnector && !hasInjected)}
                          className="w-full flex items-center gap-4 p-4 bg-white/[0.02] border border-white/[0.06] rounded-[24px] hover:bg-white/[0.04] hover:border-white/[0.14] transition-all disabled:opacity-40 shadow-inner"
                        >
                          <img
                            src={getWalletIcon(connector.name)}
                            alt={connector.name}
                            className="w-10 h-10 rounded-xl object-contain"
                          />
                          <span className="flex-1 text-left text-[15px] font-semibold text-white tracking-tight">
                            {connector.name}
                          </span>
                          {detected ? (
                            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#A3E635]/10 border border-[#A3E635]/20">
                              <Check size={12} className="text-[#A3E635]" />
                              <span className="text-[11px] font-bold text-[#A3E635]">Detected</span>
                            </div>
                          ) : (
                            <span className="px-2.5 py-1 rounded-full border border-white/10 text-[11px] font-medium text-zinc-500">
                              Not installed
                            </span>
                          )}
                        </button>
                      );
                    })}
                    {/* Coinbase app / extension sign-in also lives behind
                        the same SDK popup as the create path */}
                    {coinbase && (
                      <button
                        onClick={() => onConnect(coinbase)}
                        disabled={isLoading}
                        className="w-full flex items-center gap-4 p-4 bg-white/[0.02] border border-white/[0.06] rounded-[24px] hover:bg-white/[0.04] hover:border-white/[0.14] transition-all disabled:opacity-50 shadow-inner"
                      >
                        <img
                          src={getWalletIcon('coinbase')}
                          alt="Coinbase Wallet"
                          className="w-10 h-10 rounded-xl object-contain"
                        />
                        <span className="flex-1 text-left text-[15px] font-semibold text-white tracking-tight">
                          Coinbase Wallet
                        </span>
                        <span className="px-2.5 py-1 rounded-full border border-white/10 text-[11px] font-medium text-zinc-500">
                          App / extension
                        </span>
                      </button>
                    )}
                  </div>

                  {/* Install links only when there's genuinely nothing local */}
                  {!hasInjected && (
                    <div className="space-y-3">
                      <h4 className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider pl-1">
                        Or install a wallet extension
                      </h4>
                      {RECOMMENDED_WALLETS.map((wallet) => (
                        <button
                          key={wallet.name}
                          onClick={() => window.open(wallet.installUrl, '_blank')}
                          className="w-full flex items-center gap-4 p-4 bg-white/[0.02] border border-white/[0.06] rounded-[24px] hover:bg-white/[0.04] hover:border-white/[0.14] transition-all shadow-inner"
                        >
                          <img
                            src={wallet.icon}
                            alt={wallet.name}
                            className="w-10 h-10 rounded-xl object-contain"
                          />
                          <div className="flex-1 text-left">
                            <span className="text-[15px] font-semibold text-white tracking-tight block">
                              {wallet.name}
                            </span>
                            <span className="text-[13px] text-zinc-500">
                              {wallet.description}
                            </span>
                          </div>
                          <ExternalLink size={16} className="text-zinc-500" />
                        </button>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
export const UnlockButton = ({
  price,
  currency = 'ETH',
  recipientAddress,
  onUnlockSuccess,
  onUnlockError,
  children,
  className = '',
}) => {
  const { isConnected, connectWallet, sendPayment, authMethod, canSign } = useWallet();
  const [isProcessing, setIsProcessing] = useState(false);
  const [status, setStatus] = useState('idle');
  // an email session cannot sign (007 02c): disabled, the copy, never a payment
  const payBlocked = authMethod === 'email' && canSign !== true;

  const handleUnlock = async () => {
    if (payBlocked) return;
    try {
      if (!isConnected) {
        setStatus('connecting');
        const addr = await connectWallet();
        if (!addr) return;
      }

      setStatus('paying');
      setIsProcessing(true);

      const txHash = await sendPayment({
        to: recipientAddress,
        amount: price,
        onSuccess: (hash) => {
          setStatus('success');
          onUnlockSuccess?.(hash);
        },
        onError: (err) => {
          setStatus('error');
          onUnlockError?.(err);
        },
      });

      return txHash;
    } catch (err) {
      setStatus('error');
      onUnlockError?.(err);
    } finally {
      setIsProcessing(false);
    }
  };

  const getButtonText = () => {
    switch (status) {
      case 'connecting': return 'Connecting...';
      case 'paying': return 'Confirm in wallet...';
      case 'success': return 'Unlocked!';
      case 'error': return 'Try again';
      default: return children || `Unlock for ${price} ${currency}`;
    }
  };

  return (
    <button
      onClick={handleUnlock}
      disabled={isProcessing || status === 'success' || payBlocked}
      className={className}
      title={payBlocked ? EMAIL_PAYMENT_COPY : undefined}
    >
      {payBlocked ? EMAIL_PAYMENT_COPY : getButtonText()}
    </button>
  );
};

export default WalletProvider;